import React, { useState, useEffect } from 'react';
import {Sparkles, Star, Gift, AlertCircle, Mail, CheckCircle, Heart, Trash2, User, Upload, Edit} from 'lucide-react';
import { PrintUpload } from './components/PrintUpload';
import { ProfilePhotoUpload } from './components/ProfilePhotoUpload';
import { usePrints } from './hooks/usePrints';
import { useProfilePhoto } from './hooks/useProfilePhoto';

function App() {
  const [showUpload, setShowUpload] = useState(false);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const { prints, loading, fetchPrints, removeDuplicates, deletePrint } = usePrints();
  const { photo, loading: photoLoading, fetchPhoto, uploadPhoto, deletePhoto } = useProfilePhoto();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      await fetchPrints();
      await removeDuplicates();
      await fetchPhoto();
    };
    init();
  }, []);

  const firstTwoPrints = prints.slice(0, 2);
  const remainingPrints = prints.slice(2);

  return (
    <div className="min-h-screen bg-[#0a0118] text-white relative overflow-hidden">
      {/* Cosmos background */}
      <div className="fixed inset-0 stars-bg"></div>
      <div className="fixed inset-0 cosmic-glow"></div>

      {/* Main content */}
      <div className="relative z-10">
        {/* SEÇÃO 1 - HERO */}
        <section className="min-h-screen flex flex-col items-center justify-center px-6 py-20 text-center relative">
          <div className="absolute inset-0 radial-glow"></div>
          
          <div className="relative z-10 max-w-4xl mx-auto space-y-8 animate-fade-in">
            <div className="inline-block mb-6">
              <Sparkles className="w-16 h-16 text-[#D4AF37] animate-pulse-glow" />
            </div>

            <h1 className="font-['Playfair_Display'] text-3xl md:text-5xl lg:text-6xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-purple-200 via-white to-purple-200 bg-clip-text text-transparent">
                Leitura do Campo Vibracional
              </span>
              <br />
              <span className="text-[#D4AF37] mt-4 block md:text-4xl text-xl">(Sessão Individual de 30 minutos)

              </span>
            </h1>

            <h2 className="text-xl md:text-3xl text-purple-100 font-light leading-relaxed py-6">
              Você já percebeu que não é falta de esforço…<br />
              é falta de clareza sobre o que realmente está te bloqueando?
            </h2>

            <div className="space-y-4 text-base md:text-xl text-purple-200 leading-relaxed max-w-3xl mx-auto">
              <p>Você tenta.</p>
              <p>Você se dedica.</p>
              <p>Você evolui.</p>
              <p className="text-white font-semibold pt-4">Mas os ciclos continuam se repetindo.</p>
              <div className="pt-6 space-y-2">
                <p>Altos e baixos no dinheiro.</p>
                <p>Relações desequilibradas.</p>
                <p>Ansiedade constante.</p>
                <p>A sensação de estar travada mesmo fazendo tudo "certo".</p>
              </div>
              <p className="text-white font-medium pt-6">
                Existe algo no seu campo vibracional revelando exatamente onde sua energia está sendo drenada.
              </p>
              <p className="text-[#D4AF37] font-bold text-xl md:text-2xl pt-4">
                E é isso que essa sessão acessa.
              </p>
            </div>

            <a
              href="https://pay.hotmart.com/J104623896D"
              className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0118] px-10 py-5 rounded-full text-lg md:text-2xl font-bold shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105 transition-all duration-300 animate-pulse-subtle mt-8">

              Garantir Minha Vaga Individual
            </a>
          </div>

          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-purple-400/50 rounded-full flex items-start justify-center p-2">
              <div className="w-1.5 h-3 bg-purple-400 rounded-full animate-scroll-down"></div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 2 - O QUE É A SESSÃO */}
        <section className="py-20 px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Star className="w-12 h-12 text-[#D4AF37] mx-auto mb-6 animate-spin-slow" />
              <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-white mb-6">
                O que é a Leitura do Campo Vibracional?
              </h2>
            </div>

            <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/30 backdrop-blur-md border border-[#D4AF37]/30 rounded-3xl p-10 md:p-16 shadow-2xl space-y-8">
              <p className="text-lg md:text-xl text-purple-100 leading-relaxed">
                Um atendimento individual e online de 30 minutos para identificar:
              </p>

              <div className="space-y-6">
                {[
                'Padrões emocionais inconscientes',
                'Bloqueios energéticos ativos',
                'Áreas da vida onde sua energia está presa',
                'O que precisa ser liberado agora'].
                map((item, index) =>
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-purple-800/20 rounded-2xl border border-purple-400/20 hover:border-[#D4AF37]/50 transition-all duration-300">

                    <div className="flex-shrink-0 w-3 h-3 bg-[#D4AF37] rounded-full mt-2 animate-pulse"></div>
                    <p className="text-base md:text-xl text-white">{item}</p>
                  </div>
                )}
              </div>

              <div className="border-t border-purple-400/30 pt-8 mt-8 space-y-6">
                <p className="text-xl md:text-2xl text-white font-bold text-center">
                  Não é uma terapia longa.
                </p>
                <p className="text-base md:text-xl text-purple-100 leading-relaxed text-center">
                  É uma leitura profunda e reveladora para trazer clareza sobre a raiz do que está travando sua vida neste momento.
                </p>
                <p className="text-base md:text-xl text-[#D4AF37] font-semibold text-center">
                  Você sai da sessão com direcionamento e consciência ampliada.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 3 - COMO FUNCIONA */}
        <section className="py-20 px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <CheckCircle className="w-12 h-12 text-[#D4AF37] mx-auto mb-6 animate-pulse-glow" />
              <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-white mb-6">
                Como funciona
              </h2>
            </div>

            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/20 backdrop-blur-md border border-purple-500/20 rounded-3xl p-10 md:p-16 shadow-2xl space-y-8">
              <div className="space-y-6">
                {[
                'Atendimento individual por chamada de vídeo',
                'Leitura energética personalizada do seu campo vibracional',
                'Investigação dos padrões ativos no momento',
                'Harmonização vibracional'].
                map((item, index) =>
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-purple-800/20 rounded-2xl border border-purple-400/20 hover:border-[#D4AF37]/50 transition-all duration-300">

                    <CheckCircle className="flex-shrink-0 w-6 h-6 text-[#D4AF37] mt-1" />
                    <p className="text-base md:text-xl text-white">{item}</p>
                  </div>
                )}
              </div>

              <div className="border-t border-purple-400/30 pt-8 mt-8 text-center space-y-4">
                <p className="text-lg md:text-2xl text-white font-medium">
                  Você não sai apenas com informação.
                </p>
                <p className="text-lg md:text-2xl text-[#D4AF37] font-bold">
                  Você sai com percepção ampliada.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 3.5 - QUEM VAI CONDUZIR */}
        <section className="py-20 px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-purple-100/10 to-blue-100/10 backdrop-blur-md border border-purple-300/20 rounded-3xl p-10 md:p-16 shadow-2xl">
              <div className="text-center mb-12">
                <User className="w-12 h-12 text-[#D4AF37] mx-auto mb-6 animate-pulse-glow" />
                <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-white mb-8">
                  Quem vai conduzir sua Leitura
                </h2>
              </div>

              <div className="max-w-md mx-auto mb-8">
                {photoLoading ?
                <div className="flex justify-center py-16">
                    <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
                  </div> :
                photo ?
                <div className="relative group">
                    <img
                    src={photo.imageUrl}
                    alt="Mônica Maria"
                    className="w-full aspect-[4/5] object-cover rounded-2xl shadow-2xl border-2 border-[#D4AF37]/30" />

                    <button
                    onClick={async () => {
                      if (window.confirm('Deseja remover esta foto?')) {
                        await deletePhoto();
                      }
                    }}
                    className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-all shadow-lg opacity-0 group-hover:opacity-100">

                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div> :

                <button
                  onClick={() => setShowProfileUpload(true)}
                  className="w-full aspect-[4/5] border-2 border-dashed border-[#D4AF37]/50 rounded-2xl flex flex-col items-center justify-center gap-4 hover:border-[#D4AF37] transition-all bg-purple-900/20">

                    <Upload className="w-16 h-16 text-[#D4AF37]" />
                    <p className="text-purple-200 text-lg">Adicionar Foto Profissional</p>
                  </button>
                }
              </div>

              <div className="text-center space-y-6 max-w-2xl mx-auto">
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  Mônica Maria
                </h3>
                <p className="text-lg md:text-xl text-[#D4AF37] font-semibold">
                  Terapeuta Integrativa e Especialista em Leitura do Campo Vibracional
                </p>
                <div className="text-base md:text-lg text-purple-100 leading-relaxed space-y-4 text-left md:text-center">
                  <p>
                    Há anos conduzo mulheres que já tentaram diversas abordagens, mas ainda sentem que algo invisível bloqueia sua prosperidade, seus relacionamentos ou sua paz interior.
                  </p>
                  <p>
                    Minha abordagem une percepção energética, investigação profunda de padrões inconscientes e harmonização vibracional.
                  </p>
                  <p className="text-white font-medium">
                    Cada sessão é individual, profunda e direcionada ao momento exato que você está vivendo agora.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 4 - INDICADO PARA QUEM */}
        <section className="py-20 px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Heart className="w-12 h-12 text-[#D4AF37] mx-auto mb-6 animate-pulse-glow" />
              <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-white mb-6">
                Indicado para mulheres que:
              </h2>
            </div>

            <div className="bg-gradient-to-r from-purple-900/40 via-indigo-900/40 to-purple-900/40 backdrop-blur-md border-2 border-[#D4AF37]/40 rounded-3xl p-10 md:p-12 shadow-2xl">
              <div className="space-y-6">
                {[
                'Repetem padrões há anos',
                'Já tentaram outras abordagens e algo ainda bloqueia',
                'Vivem altos e baixos emocionais ou financeiros',
                'Estão prontas para olhar com profundidade'].
                map((item, index) =>
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-purple-800/20 rounded-2xl border border-purple-400/20 hover:border-[#D4AF37]/50 transition-all duration-300">

                    <div className="flex-shrink-0 w-3 h-3 bg-[#D4AF37] rounded-full mt-2 animate-pulse"></div>
                    <p className="text-base md:text-xl text-white">{item}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 5 - BLOCO 1 DE DEPOIMENTOS */}
        <section className="py-20 px-6 relative">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Mail className="w-12 h-12 text-[#D4AF37] mx-auto mb-6 animate-pulse-glow" />
              <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold mb-6 text-white">
                Depoimentos de Quem Já Vivenciou
              </h2>
              <p className="text-lg md:text-xl text-purple-200">
                Veja relatos reais de mulheres que decidiram olhar para a raiz dos seus bloqueios.
              </p>
            </div>

            {loading ?
            <div className="flex justify-center">
                <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
              </div> :
            firstTwoPrints.length > 0 ?
            <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                  {firstTwoPrints.map((print, index) =>
                <div
                  key={print._id}
                  className="border-2 border-purple-500/30 rounded-2xl p-4 bg-purple-900/20 backdrop-blur-sm hover:border-[#D4AF37]/50 transition-all duration-300">

                      <img
                    src={print.imageUrl}
                    alt={`Depoimento ${index + 1}`}
                    className="w-full h-auto rounded-lg shadow-2xl" />
                    </div>
                )}
                </div>
                <div className="text-center">
                  <a
                  href="https://pay.hotmart.com/J104623896D"
                  className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0118] px-10 py-5 rounded-full text-lg md:text-2xl font-bold shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105 transition-all duration-300">Garantir Minha Vaga Individual


                </a>
                </div>
              </> :

            <div className="text-center py-16 bg-purple-900/20 border border-purple-500/30 rounded-3xl">
                <p className="text-purple-300 text-lg">Nenhum depoimento publicado ainda</p>
              </div>
            }
          </div>
        </section>

        {/* SEÇÃO 6 - OFERTA */}
        <section id="oferta" className="py-20 px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#2D1B4E] to-[#1a0f2e] border-2 border-[#D4AF37] rounded-3xl p-10 md:p-16 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-center space-y-8">
                <Sparkles className="w-14 h-14 text-[#D4AF37] mx-auto animate-pulse-glow" />

                <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl font-bold text-white">
                  Leitura do Campo Vibracional<br />
                  (Sessão Individual e Online de 30 minutos)
                </h2>

                <p className="text-base md:text-xl text-purple-200 leading-relaxed">
                  Um atendimento individual para identificar bloqueios, padrões repetitivos e o que precisa ser liberado neste momento da sua vida.
                </p>

                <div className="py-8">
                  <div className="inline-block bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border-2 border-[#D4AF37] rounded-2xl px-8 py-6">
                    <p className="text-5xl md:text-7xl font-bold text-[#D4AF37] mb-3">R$ 97 à vista</p>
                    <p className="text-lg md:text-xl text-purple-200">ou 12x de R$ 10,03</p>
                  </div>
                </div>

                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-2xl px-8 py-6 max-w-2xl mx-auto space-y-4">
                  <div className="flex items-start justify-center gap-3 text-yellow-300">
                    <AlertCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                    <div className="text-left">
                      <p className="text-base md:text-lg font-semibold mb-2">Essa é uma sessão individual comigo. As vagas são limitadas por agenda.</p>
                      <p className="text-base md:text-lg">Se você sente que é o momento, confirme sua vaga agora.</p>
                    </div>
                  </div>
                </div>

                <a
                  href="https://pay.hotmart.com/J104623896D"
                  className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0118] px-12 py-6 rounded-full text-lg md:text-2xl font-bold shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105 transition-all duration-300 animate-pulse-subtle">

                  Garantir Minha Vaga Individual
                </a>

                <p className="text-base md:text-lg text-purple-200 pt-6">
                  Após realizar sua inscrição, entraremos em contato para agendar sua sessão online e enviar todas as orientações.
                </p>

                <div className="border-t border-purple-400/30 pt-8 mt-8 space-y-6">
                  <p className="text-base md:text-lg text-purple-200">
                    Ou, se você ainda tem alguma dúvida antes de confirmar, me chama no WhatsApp que eu mesma vou te orientar.
                  </p>
                  <a
                    href="https://wa.me/5581988671210?text=Quero%20agendar%20a%20minha%20Leitura%20do%20Campo%20Vibracional%20de%2030%20minutos."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#25D366] hover:bg-[#20BA5A] text-white px-10 py-4 rounded-full text-lg md:text-xl font-bold shadow-xl hover:scale-105 transition-all duration-300">

                    Falar no WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 7 - BÔNUS */}
        <section className="py-20 px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Gift className="w-16 h-16 text-[#D4AF37] mx-auto mb-6 animate-pulse-glow" />
              <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold mb-4 text-white">
                Bônus Exclusivo
              </h2>
            </div>

            <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/30 backdrop-blur-md border border-[#D4AF37]/30 rounded-3xl p-10 md:p-16 shadow-2xl space-y-8">
              <p className="text-lg md:text-2xl text-center text-white leading-relaxed">
                Ao agendar sua sessão você recebe acesso a <span className="text-[#D4AF37] font-bold">7 cursos</span> com <span className="text-[#D4AF37] font-bold">mais de 100 aulas</span> sobre:
              </p>

              <div className="space-y-4">
                {[
                'Liberação de crenças limitantes',
                'Prosperidade e desbloqueios emocionais',
                'Elevação vibracional',
                'Arquétipos e símbolos de poder',
                'Reconexão interior'].
                map((item, index) =>
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-purple-800/20 rounded-xl hover:bg-purple-800/30 transition-all duration-300">

                    <div className="flex-shrink-0 w-3 h-3 bg-[#D4AF37] rounded-full mt-2 animate-pulse"></div>
                    <p className="text-base md:text-xl text-purple-100">{item}</p>
                  </div>
                )}
              </div>

              <div className="bg-purple-800/30 border border-[#D4AF37]/50 rounded-2xl p-8 text-center">
                <p className="text-xl md:text-2xl text-white font-semibold mb-4">
                  Esses 7 cursos valeriam mais de R$ 1.500.
                </p>
                <p className="text-base md:text-xl text-[#D4AF37] font-bold">
                  Mas hoje você recebe como bônus ao confirmar sua vaga.
                </p>
              </div>

              <div className="border-t border-purple-400/30 pt-8 mt-10 space-y-4 text-center">
                <p className="text-lg md:text-2xl text-white">
                  👉 A leitura revela o seu campo.
                </p>
                <p className="text-lg md:text-2xl text-white">
                  👉 Os cursos sustentam sua transformação.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 7.5 - ACESSO IMEDIATO */}
        <section className="py-20 px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/30 backdrop-blur-md border-2 border-[#D4AF37]/50 rounded-3xl p-10 md:p-16 shadow-2xl space-y-8">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-center text-white mb-8">
                Acesso imediato após a confirmação da inscrição
              </h2>

              <div className="bg-purple-800/20 border border-purple-400/20 rounded-2xl p-8 space-y-6">
                <p className="text-lg md:text-xl text-purple-100 font-semibold">
                  Assim que seu pagamento for aprovado:
                </p>

                <div className="space-y-4">
                  {[
                  'Você recebe no seu e-mail o acesso completo aos 7 cursos',
                  'Já pode começar sua transformação no mesmo dia',
                  'Em seguida, entrarei em contato para agendar sua Leitura do Campo Vibracional (sessão individual e online de 30 minutos)'].
                  map((item, index) =>
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-purple-800/20 rounded-xl hover:bg-purple-800/30 transition-all duration-300">

                      <div className="flex-shrink-0 w-3 h-3 bg-[#D4AF37] rounded-full mt-2 animate-pulse"></div>
                      <p className="text-base md:text-lg text-white">{item}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-center pt-6">
                <a
                  href="https://pay.hotmart.com/J104623896D"
                  className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0118] px-12 py-6 rounded-full text-lg md:text-2xl font-bold shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105 transition-all duration-300 animate-pulse-subtle">

                  Garantir Minha Vaga Individual
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 8 - BLOCO 2 DE DEPOIMENTOS */}
        {remainingPrints.length > 0 &&
        <section className="py-20 px-6 relative">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-white mb-6">
                  Mais relatos de transformação
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {remainingPrints.map((print, index) =>
              <div
                key={print._id}
                className="border-2 border-purple-500/30 rounded-2xl p-4 bg-purple-900/20 backdrop-blur-sm hover:border-[#D4AF37]/50 transition-all duration-300">

                    <img
                  src={print.imageUrl}
                  alt={`Depoimento ${index + 3}`}
                  className="w-full h-auto rounded-lg shadow-2xl" />
                  </div>
              )}
              </div>
            </div>
          </section>
        }

        {/* SEÇÃO 9 - FECHAMENTO FINAL */}
        <section className="py-20 px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-purple-900/40 via-indigo-900/40 to-purple-900/40 backdrop-blur-md border-2 border-[#D4AF37]/40 rounded-3xl p-10 md:p-16 shadow-2xl text-center space-y-8">
              <Sparkles className="w-16 h-16 text-[#D4AF37] mx-auto animate-pulse-glow" />
              
              <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-white">
                Se você sente que precisa de clareza agora…
              </h2>

              <div className="space-y-4 text-lg md:text-2xl text-purple-100">
                <p>Não é sobre fazer mais.</p>
                <p className="text-white font-semibold">É sobre enxergar o que está invisível.</p>
              </div>

              <p className="text-base md:text-xl text-purple-200">
                As vagas são limitadas porque os atendimentos são individuais.
              </p>

              <div className="border-t border-purple-400/30 pt-8 mt-8 space-y-6">
                <p className="text-base md:text-lg text-purple-200">
                  Se algo dentro de você diz que é agora, mas ainda existe insegurança, me chama no WhatsApp antes de finalizar. Eu mesma vou te responder.
                </p>
                <a
                  href="https://wa.me/5581988671210?text=Quero%20agendar%20a%20minha%20Leitura%20do%20Campo%20Vibracional%20de%2030%20minutos."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-[#25D366] hover:bg-[#20BA5A] text-white px-10 py-4 rounded-full text-lg md:text-xl font-bold shadow-xl hover:scale-105 transition-all duration-300">

                  Tirar Dúvida no WhatsApp
                </a>
              </div>

              <a
                href="https://pay.hotmart.com/J104623896D"
                className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0118] px-12 py-6 rounded-full text-xl md:text-3xl font-bold shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105 transition-all duration-300 animate-pulse-subtle">

                Garantir Minha Vaga Individual
              </a>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="py-12 px-6 border-t border-purple-500/20 text-center">
          <p className="text-purple-300 text-sm">
            © 2026 Leitura do Campo Vibracional. Todos os direitos reservados.
          </p>
        </footer>
      </div>

      {/* Upload Modal */}
      {showUpload &&
      <PrintUpload
        onClose={() => {
          setShowUpload(false);
          setEditingId(null);
        }}
        onSuccess={async () => {
          if (editingId) {
            await deletePrint(editingId);
            setEditingId(null);
          }
          await fetchPrints();
          await removeDuplicates();
        }} />

      }

      {/* Profile Photo Upload Modal */}
      {showProfileUpload &&
      <ProfilePhotoUpload
        onClose={() => setShowProfileUpload(false)}
        onSuccess={async (imageUrl) => {
          await uploadPhoto(imageUrl);
        }} />

      }
    </div>);

}

export default App;